import React, { useEffect, useState } from "react";

import { Nunito, Poppins, Plus_Jakarta_Sans, Sora, Source_Serif_4 } from "next/font/google";
import { useRouter } from "next/router";

import DayJsUtils from "@date-io/dayjs";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/next";
import { GeistMono } from "geist/font/mono";
import { GeistSans } from "geist/font/sans";

const turmasNunito = Nunito({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700", "800"],
  variable: "--font-turmas-nunito",
  display: "swap",
});
const turmasSora = Sora({
  subsets: ["latin"],
  weight: ["300", "400", "700"],
  variable: "--font-turmas-sora",
  display: "swap",
});
const turmasPoppins = Poppins({
  subsets: ["latin"],
  weight: ["300", "400", "600", "700"],
  variable: "--font-turmas-poppins",
  display: "swap",
});
const turmasPlusJakarta = Plus_Jakarta_Sans({
  subsets: ["latin"],
  weight: ["400", "600", "700"],
  variable: "--font-turmas-plus-jakarta",
  display: "swap",
});
const turmasSourceSerif = Source_Serif_4({
  subsets: ["latin"],
  weight: ["400", "600", "700"],
  style: ["normal", "italic"],
  variable: "--font-turmas-source-serif",
  display: "swap",
});


import ErrorBoundary from "@/components/error-boundary";
import { GlobalQueryLoadingIndicator } from "@/components/global-query-loading-indicator";
import { LegacyRouteTransitionBoundary } from "@/components/routing/legacy-route-transition";


import dynamic from "next/dynamic";

import Client from "@/services/client";
import Settings from "@/services/settings";

import { Constants } from "../components/molecules/advanced-settings-form/constants";
import { loadChatwootScript } from "../components/organisms/players-list/load-chatwoot-script";
import {
  AuthenticationStateProvider,
  canRenderApplicationPage,
  isPublicApplicationRoute,
  type AuthenticationState,
} from "../context/authentication-state";
import { UserAplicationProvider } from "../context/useUser";
import AuthService from "../services/auth";


import "../styles/globals.css";
import "../styles/gamefik-ds.css";

import useLocalStorage from "../internal-libs/uselocalstorage";
import { TranslateAccessLevel } from "../context/onlyUserType";

import "./react-datepicker.css";
import Head from "next/head";
import Script from "next/script";

import { useClientHostname } from "@/hooks/use-client-hostname";
import { useIsMobile } from "@/hooks/use-mobile";
import { I18nProvider, useI18n } from "@/i18n/provider";
import { getDayjsLocale } from "@/i18n/format";
import { normalizeLocale } from "@/i18n/detect-locale";
import { PostHogProvider } from "@/components/providers/posthog-provider";
import { ToasterBoundary } from "@/components/ui/toaster-boundary";
import { saveFirstTouchIfMissing } from "@/lib/attribution";
import { AUTHENTICATION_CHANGED_EVENT } from "@/lib/authentication-events";
import { trackMetaPageView } from "@/lib/meta-pixel";

const ReactQueryDevtools = dynamic(
  () => import("@tanstack/react-query-devtools").then(m => m.ReactQueryDevtools),
  { ssr: false },
);
const Toaster = dynamic(
  () => import("@/components/ui/sonner").then(m => m.Toaster),
  { ssr: false },
);
const OnboardingGate = dynamic(
  () => import("@/components/modals/onboarding-gate").then(m => m.OnboardingGate),
  { ssr: false },
);
const EventInviteModal = dynamic(
  () => import("@/components/features/event-invite-modal"),
  { ssr: false },
);

const GOOGLE_CLIENT_ID = "464831750637-mvf00u3uo8e3peirdtgh30sk05dn8tce.apps.googleusercontent.com";

/** Sincroniza o idioma salvo no banco com o I18nProvider quando o usuário está autenticado. */
function LocaleSyncer({ isAuth }: { isAuth: boolean }) {
  const { setLocale } = useI18n();

  useEffect(() => {
    if (!isAuth) return;

    // Fast path: usa os dados já carregados pelo Settings.loadSettings() no login.
    const cachedLang = Settings.getSettings()?.userData?.preferredLanguage;
    if (cachedLang) {
      setLocale(normalizeLocale(cachedLang));
      return;
    }

    // Fallback: busca na API para reloads de página onde o cache pode estar vazio.
    const client = new Client();
    client.getLoggedUserData()
      .then(userData => {
        const lang = userData?.preferredLanguage;
        if (lang) {
          setLocale(normalizeLocale(lang));
        }
      })
      .catch(() => {});
  }, [isAuth, setLocale]);

  return null;
}

function MyApp({ Component, pageProps }: { Component: React.ElementType; pageProps: unknown[] }) {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <PostHogProvider>
        <I18nProvider>
          <AppContent Component={Component} pageProps={pageProps} />
        </I18nProvider>
      </PostHogProvider>
    </GoogleOAuthProvider>
  );
}

function AppContent({ Component, pageProps }: { Component: React.ElementType; pageProps: unknown[] }) {
  const router = useRouter();
  const { locale, t } = useI18n();
  const hostname = useClientHostname();

  useEffect(() => {
    saveFirstTouchIfMissing();
  }, []);

  const [alreadyLoaded, setAlreadyLoaded] = useState(false);
  const [authState, setAuthState] = useState<AuthenticationState>("checking");
  const isAuth = authState === "authenticated";

  useEffect(() => {
    const scrollToRouteHash = (url: string) => {
      const hashIndex = url.indexOf("#");
      if (hashIndex !== -1) {
        const id = url.substring(hashIndex + 1);
        setTimeout(() => {
          if (alreadyLoaded) {
            return;
          }
          setAlreadyLoaded(true);
          const element = document.getElementById(id);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 900);
      }
    };

    const handleRouteChangeComplete = (url: string) => {
      trackMetaPageView();
      scrollToRouteHash(url);
    };

    router.events.on("routeChangeComplete", handleRouteChangeComplete);

    scrollToRouteHash(window.location.href);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
  }, [router.events]);

  const checkPublicRoutes = () => {
    const currentHostname = hostname || (typeof window !== "undefined" ? window.location.hostname : null);
    if (currentHostname === "localhost" && router.pathname === "/signup/plans") return true;
    return isPublicApplicationRoute(router.pathname);
  };

  const checkIsAuthenticated = async () => {
    const resAuth = await AuthService.isAuthenticated();
    if (resAuth) {
      setAuthState("authenticated");
    } else {
      setAuthState("anonymous");
    }

    if (!resAuth && !checkPublicRoutes()) {
      router.push("/login");
    }
  };

  const [userAccessLevel] = useLocalStorage(Constants.LSK_UserAccessLevel, 0);

  useEffect(() => {
    checkIsAuthenticated();
    if (userAccessLevel && typeof window !== "undefined" && window.location.pathname !== "/login") {
      loadChatwootScript(undefined, TranslateAccessLevel(userAccessLevel));
    }
  }, [userAccessLevel]);

  useEffect(() => {
    const refreshAuthentication = () => {
      void checkIsAuthenticated();
    };
    window.addEventListener(AUTHENTICATION_CHANGED_EVENT, refreshAuthentication);
    return () => window.removeEventListener(AUTHENTICATION_CHANGED_EVENT, refreshAuthentication);
  }, [router.pathname]);

  const queryClient = React.useMemo(() => {
    return new QueryClient();
  }, []);

  const isMobile = useIsMobile();
  const isPublicRoute = Boolean(checkPublicRoutes());
  const canRenderPage = canRenderApplicationPage(authState, isPublicRoute);

  return (
    <MuiPickersUtilsProvider locale={getDayjsLocale(locale)} utils={DayJsUtils}>
        {/* Expoe as variáveis de fonte do next/font no :root para que conteúdo
            renderizado via createPortal (document.body) também herde Geist/Nunito,
            mantendo paridade visual 1:1 com new-manager. */}
        <style jsx global>{`
          :root {
            --font-turmas-geist: ${GeistSans.style.fontFamily};
            --font-turmas-nunito: ${turmasNunito.style.fontFamily};
            --font-turmas-source-serif: ${turmasSourceSerif.style.fontFamily};
            --font-geist-mono: ${GeistMono.style.fontFamily};
          }
        `}</style>
        <div className={`${GeistSans.variable} ${turmasNunito.variable} ${GeistMono.variable} ${turmasSora.variable} ${turmasPoppins.variable} ${turmasPlusJakarta.variable} ${turmasSourceSerif.variable}`}>
        <Head>
          <title>{t("app.defaultTitle")}</title>
          {/* Sobrescreve a viewport default do Next (que é só
              `width=device-width`). O que precisamos a mais é o
              `viewport-fit=cover`: sem ele o `env(safe-area-inset-*)` devolve
              0 em aparelho com notch e a bottom nav encosta no indicador de
              home do iPhone. Fica aqui, e não no _document, porque lá o Next
              avisa (no-document-viewport-meta) e ainda emite a default por
              cima, servindo duas tags. */}
          <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
          <link rel="manifest" href="/manifest.json" />
        </Head>
        {hostname === "localhost" ? <Script src="//unpkg.com/react-scan/dist/auto.global.js" crossOrigin="anonymous" strategy="afterInteractive" /> : null}
        <ErrorBoundary>
          <SpeedInsights />
          <QueryClientProvider client={queryClient}>
            <ReactQueryDevtools initialIsOpen={false} position="right" buttonPosition="bottom-left" />
            <GlobalQueryLoadingIndicator />
            <AuthenticationStateProvider value={authState}>
              <UserAplicationProvider isAuth={isAuth}>
                <LocaleSyncer isAuth={isAuth} />
                {canRenderPage ? (
                  <LegacyRouteTransitionBoundary>
                    <Component {...pageProps} />
                  </LegacyRouteTransitionBoundary>
                ) : null}
                {isAuth ? <OnboardingGate /> : null}
                {isAuth ? <EventInviteModal /> : null}
              {/* Boundary dedicado: um toast com conteúdo não renderizável não
                  pode derrubar o app inteiro no ErrorBoundary global. */}
              <ToasterBoundary>
                <Toaster
                  position={isMobile && router.pathname === "/details" ? "bottom-left" : "bottom-right"}
                  duration={2000}
                />
              </ToasterBoundary>
              <script
                dangerouslySetInnerHTML={{
                  __html: `
                  (function(c,l,a,r,i,t,y){
                    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                    })(window, document, "clarity", "script", "lqh11nua5t");
                    `,
                }}
              />
              <Script id="meta-pixel" strategy="afterInteractive">
                {`
                  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                  n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
                  n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
                  t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
                  (window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
                  fbq('set', 'autoConfig', false, '1905956446691628');
                  fbq('init', '1905956446691628');
                  fbq('track', 'PageView');
                `}
              </Script>
              <Script
                src="https://www.googletagmanager.com/gtag/js?id=G-X09T58BWKR"
                strategy="afterInteractive"
              />
              <Script id="google-analytics" strategy="afterInteractive">
                {`
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', 'G-X09T58BWKR');
                `}
              </Script>
                <Analytics />
              </UserAplicationProvider>
            </AuthenticationStateProvider>
          </QueryClientProvider>
        </ErrorBoundary>
        </div>
      </MuiPickersUtilsProvider>
  );
}

String.prototype.normalizeText = function (): string {
  return this.normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
};

Array.prototype.toReversed = function (): any[] {
  return [...this].reverse();
};

declare global {
  interface String {
    normalizeText(): string;
  }
}

export default MyApp;
